<template>
    <section class="mb-6 flex flex-col gap-2">
        <div class="font-semibold flex items-center gap-2">
            <span class="inline-flex items-center justify-center w-5 h-5 rounded-full border border-primary text-primary text-xs shrink-0">1</span>
            {{ $t('courseframe.courseStepLabel') }}
        </div>
        <div class="text-xs text-base-content-subtle leading-5">
            {{ $t('courseframe.courseStepDescription') }}
        </div>
        <label for="courseframe-course-input" class="form-control w-full">
            <input
                id="courseframe-course-input"
                ref="courseInputEl"
                v-model="searchTerm"
                type="text"
                :form="formId"
                :placeholder="$t('courseframe.courseInputPlaceholder')"
                class="input input-bordered w-full text-sm"
                required
                data-cy="courseframe-course-input"
                @input="onManualInput"
            >
        </label>
    </section>
</template>

<script setup lang="ts">
/// <reference types="@types/google.maps" />

defineProps<{
    formId: string
}>()

const { product } = useProduct()
const { searchTerm, lookupCoordinate, clearMatch } = useCourseframe()
const { initPlacesLibrary, initAutocomplete, place } = usePlaceAutocomplete()

const courseInputEl = ref<HTMLInputElement | null>(null)

onMounted(async () => {
    await initPlacesLibrary()
    if (courseInputEl.value != null) {
        initAutocomplete(courseInputEl.value, ['establishment'])
    }
})

watch(place, (newVal) => {
    if (newVal == null) return

    const placeResult = newVal as google.maps.places.PlaceResult
    const latitude = placeResult.geometry?.location?.lat()
    const longitude = placeResult.geometry?.location?.lng()

    if (latitude == null || longitude == null) return

    // Google's widget writes the selected suggestion's text into the input before firing
    // place_changed — submit exactly that, not placeResult.name, so searchTerm always
    // matches what the customer sees in the field.
    searchTerm.value = courseInputEl.value?.value ?? placeResult.name ?? searchTerm.value
    lookupCoordinate(product.value.id, latitude, longitude)
})

// A course the customer types without picking a suggestion is allowed: Google does not list every
// golf course, and the backend accepts the search term on its own
function onManualInput () {
    clearMatch()
}
</script>
